import { ContainerBloco, TituloBloco } from "../../../Demanda/Common/styles";
import { Container } from "../styles";
import { Clock } from "lucide-react";
import { comum } from "../../../../styles/Themes/light";
import { IItemHistoricoPlano } from "../types";
import ItemHistorico from "../ItemHistorico";
import SkeletonItemHistorico from "../ItemHistorico/SkeletonItemHistorico/index";
import HistoricoVazio from "../HistoricoVazio/index";
import HistoricoErro from "../HistoricoErro/index";
import { useEffect, useState, useCallback } from "react";
import api from "../../../../services/api";
import { formatServicoColuna } from "../../../../utils/servicoMapperPlanos";

const JANELA_AGRUPAMENTO_MS = 5000;

const isServicoAdicionado = (acao: string): boolean => {
  const a = (acao || '').toLowerCase().trim();
  return a === 'serviço adicionado' || a.includes('serviço adicionado');
};

/**
 * Agrupa itens "Serviço adicionado" que ocorreram na mesma janela (ex.: criação de plano com N serviços).
 * Planos antigos podem ter N registros separados; agrupa para exibir como um único card.
 */
function agruparHistoricoPlano(itens: IItemHistoricoPlano[]): IItemHistoricoPlano[] {
  const resultado: IItemHistoricoPlano[] = [];
  let i = 0;

  while (i < itens.length) {
    const item = itens[i];
    if (!isServicoAdicionado(item.acao) || item.origem_evento === 'TEMPLATE_GLOBAL') {
      resultado.push(item);
      i++;
      continue;
    }

    const grupo: IItemHistoricoPlano[] = [item];
    const t0 = new Date(item.criado).getTime();
    const responsavel = item.nome_responsavel || '';

    while (i + 1 < itens.length) {
      const prox = itens[i + 1];
      if (!isServicoAdicionado(prox.acao) || prox.origem_evento === 'TEMPLATE_GLOBAL') break;
      if ((prox.nome_responsavel || '') !== responsavel) break;
      const tProx = new Date(prox.criado).getTime();
      if (t0 - tProx > JANELA_AGRUPAMENTO_MS) break;
      grupo.push(prox);
      i++;
    }

    if (grupo.length === 1) {
      resultado.push(item);
    } else {
      const servicos = grupo.map((g) => {
        const p = typeof g.payload === 'string' ? JSON.parse(g.payload || '{}') : g.payload || {};
        const nomeServico =
          p.area_direito || p.servico || p.tipo_audiencia
            ? formatServicoColuna(p.area_direito, p.servico, p.tipo_audiencia)
            : 'Serviço';
        return {
          ...p,
          servico: p.servico,
          area_direito: p.area_direito,
          tipo_audiencia: p.tipo_audiencia,
          nome_servico: nomeServico,
        };
      });
      resultado.push({
        ...grupo[0],
        id: grupo[0].id,
        payload: { servicos },
      });
    }
    i++;
  }

  return resultado;
}

interface AbaHistoricoPlanoProps {
  abaAtiva: string;
  planoId?: number;
  loading?: boolean;
}

export default function AbaHistoricoPlano({ abaAtiva, planoId, loading: loadingParent }: AbaHistoricoPlanoProps) {
  const [historico, setHistorico] = useState<IItemHistoricoPlano[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);

  const carregaHistorico = useCallback(async () => {
    if (!planoId) {
      setLoading(false);
      setErro(false);
      return;
    }

    try {
      setLoading(true);
      setErro(false);
      const response = await api.get(`/plano-solicitante-template/${planoId}/historico`);

      if (response.data.status === 'error') {
        throw new Error(response.data.message || 'Erro ao carregar histórico');
      }

      if (response.data.status === 'success') {
        const historicoData = response.data.result || [];
        
        const historicoMapeado: IItemHistoricoPlano[] = historicoData
          .map((item: any) => ({
            id: item.id || item.id_historico_plano_solicitante_template,
            id_historico_plano_solicitante_template: item.id || item.id_historico_plano_solicitante_template,
            criado: item.criado || '',
            acao: item.acao || '',
            acao_original: item.acao || '',
            origem_evento: item.origem_evento || 'LOCAL',
            nome_responsavel: item.nome_responsavel || '',
            tipo_responsavel: item.tipo_responsavel || '',
            dispositivo_tipo: item.dispositivo_tipo || 'desktop',
            payload: item.payload || null,
          }))
          .sort((a: IItemHistoricoPlano, b: IItemHistoricoPlano) => {
            return (b.id || 0) - (a.id || 0);
          });

        setHistorico(agruparHistoricoPlano(historicoMapeado));
      }
    } catch (error: any) {
      console.error('Erro ao carregar histórico:', error);
      setErro(true);
      setHistorico([]);
    } finally {
      setLoading(false);
    }
  }, [planoId]);

  useEffect(() => {
    if (abaAtiva === 'historico') {
      carregaHistorico();
    }
  }, [abaAtiva, carregaHistorico]);

  if (abaAtiva !== 'historico') {
    return null;
  }

  return (
    <Container>
      <ContainerBloco>
        <TituloBloco>
          <Clock size={16} color={comum.colors.primaryRed400}/>
          Histórico do Plano
        </TituloBloco>

        <div className="historico">
          {(loading || loadingParent) ? (
            [1, 2, 3, 4, 5].map((index) => (
              <SkeletonItemHistorico key={index} lastItem={index === 5} />
            ))
          ) : erro ? (
            <HistoricoErro onRetry={carregaHistorico} />
          ) : historico.length > 0 ? (
            historico.map((item, index) => (
              <ItemHistorico key={item.id} item={item} lastItem={index === historico.length - 1} />
            ))
          ) : (
            <HistoricoVazio />
          )}
        </div>
      </ContainerBloco>
    </Container>
  );
}
